// ABOUTME: Scheduled emails tab for admin email campaign management
// ABOUTME: Displays and manages scheduled campaign emails with filtering
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import { Refresh, Cancel } from '@mui/icons-material';
import axios from 'axios';
import { formatAbsoluteDateTime, formatRelativeTime } from '../../utils/timeUtils';
import { getCurrentAccessToken } from '../../services/api';

interface ScheduledEmailDto {
  id: string;
  scheduledTimeUTC: string;
  scheduledTimeInFamilyTimezone: string;
  familyName: string;
  familyTimezone: string;
  familyId: number;
  campaignName: string;
  stepName: string;
  recipientEmail: string;
  status: string;
  templateKey: string;
}

interface PagedResponse {
  data: ScheduledEmailDto[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

interface FamilyOption {
  id: number;
  name: string;
}

interface CampaignOption {
  id: number;
  name: string;
}

export default function ScheduledEmailsTab() {
  const [scheduledEmails, setScheduledEmails] = useState<ScheduledEmailDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Filter states
  const [familyId, setFamilyId] = useState<number | ''>('');
  const [campaignId, setCampaignId] = useState<number | ''>('');
  const [status, setStatus] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [templateKey, setTemplateKey] = useState<string>('');

  // Dropdown options
  const [families, setFamilies] = useState<FamilyOption[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'resend' | 'cancel' | null;
    emailId: string | null;
    emailDetails: string;
  }>({
    open: false,
    action: null,
    emailId: null,
    emailDetails: '',
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchScheduledEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchScheduledEmails is a plain function in component scope; the effect correctly re-runs when the listed deps change, not when the function reference changes
  }, [page, pageSize, familyId, campaignId, status, fromDate, toDate, templateKey]);

  const fetchFilterOptions = async () => {
    try {
      const token = getCurrentAccessToken();
      const headers = { Authorization: `Bearer ${token}` };
      const baseUrl = import.meta.env.VITE_API_URL;

      // Fetch families
      const familiesResponse = await axios.get<{ data: FamilyOption[] }>(
        `${baseUrl}/api/admin/families`,
        {
          params: { page: 1, pageSize: 1000, paginate: false },
          headers,
        }
      );
      setFamilies(familiesResponse.data.data || []);

      // Fetch campaigns
      const campaignsResponse = await axios.get<{
        data: CampaignOption[];
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
      }>(
        `${baseUrl}/api/admin/email-campaigns`,
        {
          params: { page: 1, pageSize: 100 },
          headers,
        }
      );
      setCampaigns((campaignsResponse.data.data || []).map(c => ({ id: c.id, name: c.name })));
    } catch (err: unknown) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchScheduledEmails = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getCurrentAccessToken();
      const params: {
        page: number;
        pageSize: number;
        familyId?: number;
        campaignId?: number;
        status?: string;
        fromDate?: string;
        toDate?: string;
        templateKey?: string;
      } = {
        page: page + 1, // Backend uses 1-based pagination
        pageSize: pageSize,
      };

      if (familyId) params.familyId = familyId;
      if (campaignId) params.campaignId = campaignId;
      if (status) params.status = status;
      if (fromDate) params.fromDate = new Date(fromDate).toISOString();
      if (toDate) params.toDate = new Date(toDate).toISOString();
      if (templateKey) params.templateKey = templateKey;

      const response = await axios.get<PagedResponse>(
        `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/admin/email-campaigns/scheduled`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setScheduledEmails(response.data.data);
      setTotalCount(response.data.totalCount);
    } catch (err: unknown) {
      console.error('Error fetching scheduled emails:', err);
      setError((axios.isAxiosError(err) ? err.response?.data?.message : undefined) || 'Failed to fetch scheduled emails');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openConfirmDialog = (
    action: 'resend' | 'cancel',
    emailId: string,
    emailDetails: string
  ) => {
    setConfirmDialog({
      open: true,
      action,
      emailId,
      emailDetails,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      action: null,
      emailId: null,
      emailDetails: '',
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog.emailId || !confirmDialog.action) return;

    try {
      const token = getCurrentAccessToken();
      const baseUrl = import.meta.env.VITE_API_URL;
      const endpoint = `${baseUrl}/api/admin/email-campaigns/scheduled/${confirmDialog.emailId}/${confirmDialog.action}`;

      await axios.post(
        endpoint,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      closeConfirmDialog();
      fetchScheduledEmails(); // Refresh the list
    } catch (err: unknown) {
      console.error(`Error ${confirmDialog.action}ing email:`, err);
      setError((axios.isAxiosError(err) ? err.response?.data?.message : undefined) || `Failed to ${confirmDialog.action} email`);
      closeConfirmDialog();
    }
  };

  const getStatusColor = (status: string): 'default' | 'success' | 'error' | 'warning' => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const clearFilters = () => {
    setFamilyId('');
    setCampaignId('');
    setStatus('');
    setFromDate('');
    setToDate('');
    setTemplateKey('');
  };

  if (loading && scheduledEmails.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Family</InputLabel>
              <Select
                value={familyId}
                label="Family"
                onChange={(e) => setFamilyId(e.target.value as number | '')}
              >
                <MenuItem value="">All Families</MenuItem>
                {families.map((family) => (
                  <MenuItem key={family.id} value={family.id}>
                    {family.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Campaign</InputLabel>
              <Select
                value={campaignId}
                label="Campaign"
                onChange={(e) => setCampaignId(e.target.value as number | '')}
              >
                <MenuItem value="">All Campaigns</MenuItem>
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Sent">Sent</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Template Type</InputLabel>
              <Select
                value={templateKey}
                label="Template Type"
                onChange={(e) => setTemplateKey(e.target.value)}
              >
                <MenuItem value="">All Templates</MenuItem>
                <MenuItem value="Welcome">Welcome</MenuItem>
                <MenuItem value="PaymentFailed">Payment Failed</MenuItem>
                <MenuItem value="PaymentRecovered">Payment Recovered</MenuItem>
                <MenuItem value="SubscriptionCancelled">Subscription Cancelled</MenuItem>
                <MenuItem value="SubscriptionRefunded">Subscription Refunded</MenuItem>
                <MenuItem value="SubscriptionResumed">Subscription Resumed</MenuItem>
                <MenuItem value="SubscriptionExpired">Subscription Expired</MenuItem>
                <MenuItem value="SubscriptionPaused">Subscription Paused</MenuItem>
                <MenuItem value="SubscriptionUnpaused">Subscription Unpaused</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <Button variant="outlined" onClick={clearFilters} fullWidth>
              Clear Filters
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table aria-label="scheduled emails table">
          <TableHead>
            <TableRow>
              <TableCell>Scheduled Time</TableCell>
              <TableCell>Family</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell>Step / Template</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scheduledEmails.map((email) => (
              <TableRow key={email.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {formatAbsoluteDateTime(email.scheduledTimeInFamilyTimezone)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatRelativeTime(email.scheduledTimeUTC, email.familyTimezone)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={`Timezone: ${email.familyTimezone}`}>
                    <span>{email.familyName}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{email.campaignName}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{email.stepName}</Typography>
                    {email.templateKey && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {email.templateKey}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{email.recipientEmail}</TableCell>
                <TableCell>
                  <Chip
                    label={email.status}
                    size="small"
                    color={getStatusColor(email.status)}
                  />
                </TableCell>
                <TableCell align="center">
                  {email.status === 'Pending' && (
                    <>
                      <Tooltip title="Cancel Email">
                        <IconButton
                          size="small"
                          onClick={() =>
                            openConfirmDialog(
                              'cancel',
                              email.id,
                              `${email.campaignName} - ${email.stepName} to ${email.recipientEmail}`
                            )
                          }
                        >
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {(email.status === 'Failed' || email.status === 'Cancelled') && (
                    <Tooltip title="Resend Email">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openConfirmDialog(
                            'resend',
                            email.id,
                            `${email.campaignName} - ${email.stepName} to ${email.recipientEmail}`
                          )
                        }
                      >
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
        <DialogTitle>
          Confirm {confirmDialog.action === 'resend' ? 'Resend' : 'Cancel'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmDialog.action} this email?
            <br />
            <strong>{confirmDialog.emailDetails}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>No, Go Back</Button>
          <Button onClick={handleConfirmAction} color="primary" variant="contained">
            Yes, {confirmDialog.action === 'resend' ? 'Resend' : confirmDialog.action === 'cancel' ? 'Cancel' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
